<template>
  <b-overlay :show="loading">
    <div
      class="h-screen flex w-full bg-img vx-row no-gutter items-center justify-center"
      id="management"
      v-bind:style="{ 'background-image': 'url(' + imagenFondo + ') !important',
      'background-attachment': 'fixed',
      overflow: 'scroll'
    }"
    >
      <div>
        <img
          v-if="ingresoLogo"
          :src="ingresoLogo.imagen"
          id="logo"
          v-bind:style="logoStyle"
          alt="logo"
        />

        <!-- Flujo e-Sign7 (original) -->
        <template v-if="provider === 'esign7'">
          <h4 class="mb-5 text-white text-center">
            Verificación de identidad para Esign7
          </h4>
          <b-card>
            <verification-form-external
              :cuUserData="cuUserData"
            />
          </b-card>
        </template>

        <!-- Flujo SignApi: selección de método de verificación -->
        <template v-if="provider === 'signapi'">
          <h4 class="mb-5 text-white text-center">
            Verificación de identidad
          </h4>
          <b-card style="min-width: 450px;">
            <div v-if="!verificationStarted">
              <div class="text-center mb-4">
                <p class="mb-2"><strong>{{ cuUserData.name }} {{ cuUserData.lastName }}</strong></p>
                <p class="text-muted">RUT: {{ cuUserData.iut }}</p>
              </div>
              <p class="text-center mb-4">
                Seleccione el método de verificación de identidad para generar su certificado de firma electrónica avanzada.
              </p>
              <div class="row px-3">
                <div class="col-12 mb-4" v-if="nfcEnabled">
                  <b-button
                    variant="primary"
                    block
                    size="lg"
                    @click="startVerification('NFC')"
                  >
                    <feather-icon icon="SmartphoneIcon" class="mr-2" />
                    Verificación por NFC
                  </b-button>
                  <div class="bg-light rounded p-3 mt-2" style="border-left: 3px solid #7367f0;">
                    <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
                      Se deberá descargar una aplicación para realizar una verificación de identidad con su dispositivo móvil.
                    </p>
                    <p class="mb-0" style="color: #4e4e4e; font-size: 0.85rem;">
                      <strong>Su dispositivo debe contar con sensor NFC para continuar con esta opción.</strong>
                    </p>
                  </div>
                </div>
                <div class="col-12 mb-3" v-if="bankTransferEnabled">
                  <b-button
                    variant="outline-primary"
                    block
                    size="lg"
                    @click="startVerification('BANK_TRANSFER')"
                  >
                    <feather-icon icon="DollarSignIcon" class="mr-2" />
                    Verificación por Transferencia bancaria
                  </b-button>
                  <div class="bg-light rounded p-3 mt-2" style="border-left: 3px solid #7367f0;">
                    <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
                      Se debe realizar una transferencia bancaria desde una cuenta a su nombre.
                    </p>
                    <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
                      <strong>El RUT del titular de la cuenta debe ser igual a {{ cuUserData.iut }}.</strong>
                    </p>
                    <p class="mb-0" style="color: #4e4e4e; font-size: 0.85rem;">
                      El monto será reversado una vez confirmada su identidad.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="verificationStarted" class="text-center">
              <!-- Vista de fallo -->
              <template v-if="verificationFailed">
                <div class="mb-4">
                  <feather-icon icon="XCircleIcon" size="48" class="text-danger" />
                </div>
                <h5 class="text-danger">Verificación fallida</h5>
                <p style="color: #4e4e4e; font-size: 0.95rem;">
                  No fue posible verificar su identidad. Esto puede ocurrir si los datos ingresados no coinciden o si hubo un problema con el proceso de verificación.
                </p>
                <div class="bg-light rounded p-3 mb-3 text-left" style="border-left: 3px solid #ea5455;">
                  <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
                    <strong>Importante:</strong> El sistema permite un máximo de 3 intentos de verificación cada 24 horas.
                    Si ha agotado sus intentos, deberá esperar antes de volver a intentarlo.
                  </p>
                </div>
                <p style="color: #4e4e4e; font-size: 0.95rem;">
                  Si el problema persiste, contacte a soporte para obtener asistencia.
                </p>
                <div class="mt-4">
                  <b-button
                    variant="primary"
                    @click="retryVerification"
                  >
                    <feather-icon icon="RefreshCwIcon" class="mr-1" />
                    Intentar nuevamente
                  </b-button>
                </div>
              </template>

              <!-- Vista NFC: contador + descarga app + preguntas -->
              <template v-else-if="verificationMethod === 'NFC'">
                <!-- Cargando preguntas -->
                <template v-if="loadingQuestions">
                  <div class="mb-4">
                    <feather-icon icon="CheckCircleIcon" size="48" class="text-success" />
                  </div>
                  <h5>Identidad verificada por NFC</h5>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    Estamos solicitando las preguntas de verificación. Espere un momento.
                  </p>
                  <div class="mt-3">
                    <b-spinner variant="primary" label="Cargando preguntas..." />
                  </div>
                </template>

                <!-- Preguntas de seguridad (después de NFC validada) -->
                <template v-else-if="showSecurityQuestions">
                  <div class="text-left">
                    <h5 class="mb-3">Preguntas de verificación de identidad</h5>
                    <p style="color: #4e4e4e; font-size: 0.9rem;" class="mb-4">
                      Su identidad fue verificada por NFC. Responda las siguientes preguntas para completar el proceso.
                    </p>
                    <div v-for="(pregunta, index) in securityQuestions" :key="index" class="mb-4">
                      <label class="font-weight-bold" style="color: #4e4e4e;">
                        {{ pregunta.questionText || pregunta.question || pregunta.text || pregunta }}
                      </label>
                      <b-form-radio-group
                        v-if="pregunta.answerChoice || pregunta.options || pregunta.alternatives"
                        v-model="securityAnswers[index]"
                        :options="formatQuestionOptions(pregunta.answerChoice || pregunta.options || pregunta.alternatives)"
                        stacked
                        class="mt-2"
                      />
                      <b-form-input
                        v-else
                        v-model="securityAnswers[index]"
                        class="mt-2"
                        placeholder="Ingrese su respuesta"
                      />
                    </div>
                    <div class="text-right mt-4">
                      <b-button
                        variant="primary"
                        :disabled="!allSecurityQuestionsAnswered || loading"
                        @click="submitSecurityAnswers"
                      >
                        Verificar y generar certificado
                      </b-button>
                    </div>
                  </div>
                </template>

                <!-- Generando certificado (después de responder preguntas) -->
                <template v-else-if="certificateQueued">
                  <div class="mb-4">
                    <feather-icon icon="ShieldIcon" size="48" class="text-warning" />
                  </div>
                  <h5>Generando certificado de firma</h5>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    Sus respuestas fueron validadas correctamente. Estamos generando su certificado de firma electrónica avanzada.
                  </p>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    <strong>No cierre esta página.</strong> Será redirigido automáticamente en unos segundos.
                  </p>
                  <div class="mt-3">
                    <b-spinner variant="warning" label="Generando certificado..." />
                  </div>
                </template>

                <!-- Contador + descarga app (esperando NFC) -->
                <template v-else>
                  <div class="mb-3">
                    <feather-icon icon="SmartphoneIcon" size="42" class="text-primary" />
                  </div>
                  <h5>Verificación NFC en proceso</h5>

                  <!-- Contador regresivo -->
                  <div class="my-4">
                    <div class="d-inline-block rounded-circle border border-primary p-3" style="width: 100px; height: 100px; line-height: 70px;">
                      <span class="font-weight-bold" style="font-size: 1.5rem; color: #7367f0;">
                        {{ countdownFormatted }}
                      </span>
                    </div>
                    <p class="text-muted mt-2 small">Tiempo restante para completar la verificación</p>
                  </div>

                  <div class="bg-light rounded p-3 mb-4 text-left" style="border-left: 3px solid #7367f0;">
                    <p class="mb-2" style="color: #4e4e4e; font-size: 0.9rem;">
                      <strong>Descargue la aplicación Firmaki ID</strong> en su celular para completar la verificación NFC con su cédula de identidad.
                    </p>
                  </div>

                  <!-- Links de descarga -->
                  <div class="row mt-3">
                    <div class="col-6 text-center">
                      <a
                        href="https://apps.apple.com/cl/app/firmaki-id/id6756390157"
                        target="_blank"
                        class="btn btn-outline-dark btn-block"
                      >
                        <feather-icon icon="SmartphoneIcon" size="14" class="mr-1" />
                        App Store (iPhone)
                      </a>
                    </div>
                    <div class="col-6 text-center">
                      <a
                        href="https://play.google.com/store/apps/details?id=id.amparo.firmaki"
                        target="_blank"
                        class="btn btn-outline-success btn-block"
                      >
                        <feather-icon icon="SmartphoneIcon" size="14" class="mr-1" />
                        Google Play (Android)
                      </a>
                    </div>
                  </div>

                  <p v-if="countdownExpired" class="text-danger mt-4 font-weight-bold">
                    El tiempo ha expirado. Por favor, inicie el proceso nuevamente.
                  </p>
                </template>
              </template>

              <!-- Vista Transferencia bancaria u otros -->
              <template v-else>
                <!-- Generando certificado -->
                <template v-if="certificateQueued">
                  <div class="mb-4">
                    <feather-icon icon="ShieldIcon" size="48" class="text-warning" />
                  </div>
                  <h5>Generando certificado de firma</h5>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    Su identidad fue verificada correctamente. Estamos generando su certificado de firma electrónica avanzada.
                  </p>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    <strong>No cierre esta página.</strong> Será redirigido automáticamente en unos segundos.
                  </p>
                  <div class="mt-3">
                    <b-spinner variant="warning" label="Generando certificado..." />
                  </div>
                </template>
                <!-- Verificación en proceso (esperando banco) -->
                <template v-else>
                  <div class="mb-4">
                    <feather-icon icon="ClockIcon" size="48" class="text-primary" />
                  </div>
                  <h5>Verificación en proceso</h5>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    Estamos verificando su transferencia bancaria. Este proceso puede demorar unos segundos.
                  </p>
                  <p style="color: #4e4e4e; font-size: 0.95rem;">
                    <strong>No cierre esta página.</strong> Será redirigido automáticamente una vez confirmada su identidad.
                  </p>
                  <div class="mt-3">
                    <b-spinner variant="primary" label="Verificando..." />
                  </div>
                </template>
              </template>
            </div>
          </b-card>
        </template>
      </div>
    </div>
  </b-overlay>
</template>
<script>

import AvanzadaSignEsign7External
  from "@/febos/global/usuario/components/sign/advanced/eSign/AvanzadaSignEsign7External";
import VerificationFormExternal from "@/febos/global/usuario/components/sign/advanced/eSign/components/VerificationFormExternal";
import {mapActions} from "vuex";
import { cuVerificationInfoExternal } from "@/febos/servicios/api/v2.aprobaciones.api";
import PersonalizacionMixin from '@/febos/global/empresas/mixins/PersonalizacionMixin';
import { decodeJwt } from '@/febos/global/utils/jwt';

export default {
  components: { VerificationFormExternal, AvanzadaSignEsign7External },
  mixins: [PersonalizacionMixin],
  data() {
    return {
      provider: 'esign7',
      jwtData: {},
      cuUserData: {},
      loading: false,
      nfcEnabled: true,
      bankTransferEnabled: true,
      showQuestions: true,
      getCertificate: true,
      securityQuestions: {},
      stampUrl: '',
      verificationStarted: false,
      verificationFailed: false,
      verificationErrorStatus: '',
      certificateQueued: false,
      showSecurityQuestions: false,
      loadingQuestions: false,
      securityQuestions: [],
      securityAnswers: {},
      verificationMethod: '',
      authUrl: '',
      countdown: 600,
      countdownInterval: null,
      countdownExpired: false,
      pollingInterval: null,
      qrAppleStore: '',
      qrPlayStore: '',
    };
  },
  computed: {
    token() {
      const { token } = this.$route.params;
      return token;
    },
    verificationMethodLabel() {
      if (this.verificationMethod === 'NFC') return 'NFC';
      if (this.verificationMethod === 'BANK_TRANSFER') return 'Transferencia bancaria';
      return this.verificationMethod;
    },
    countdownFormatted() {
      const min = Math.floor(this.countdown / 60);
      const sec = this.countdown % 60;
      return `${min}:${sec.toString().padStart(2, '0')}`;
    },
    allSecurityQuestionsAnswered() {
      if (!this.securityQuestions || this.securityQuestions.length === 0) return false;
      return this.securityQuestions.every((_, index) =>
        this.securityAnswers[index] !== undefined && this.securityAnswers[index] !== null && this.securityAnswers[index] !== ''
      );
    }
  },
  methods: {
    ...mapActions('Approvals', [
      'getCUInfo'
    ]),
    async startVerification(method) {
      this.loading = true;
      this.verificationMethod = method;
      const { idProceso } = this.$route.query;

      try {
        const response = await cuVerificationInfoExternal({
          params: {
            tipo: 'signapi',
            accion: 'request_2fa',
            metodo2fa: method,
          },
          headers: {
            Authorization: this.token,
          },
          body: {
            idProceso,
          }
        });

        this.loading = false;
        this.verificationStarted = true;

        // Si el usuario ya fue validado, ir directo a firma
        if (response.data && response.data.alreadyValidated) {
          this.verificationSuccess();
          return;
        }

        if (method === 'NFC') {
          this.startCountdown();
          this.generateQRCodes();
          this.startPolling();
        }

        // Si SignApi retorna una authUrl para redirigir (transferencia bancaria)
        if (response.data && response.data.authUrl && method !== 'NFC') {
          this.authUrl = response.data.authUrl;
          window.location.href = response.data.authUrl;
        }
        if (response.data && response.data.authUrl && method === 'NFC') {
          this.authUrl = response.data.authUrl;
        }
      } catch (error) {
        this.loading = false;
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al iniciar verificación',
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            noAutoHide: true
          }
        );
      }
    },
    startCountdown() {
      this.countdown = 600;
      this.countdownExpired = false;
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
          this.countdownExpired = true;
        }
      }, 1000);
    },
    generateQRCodes() {
      // QR codes generados como data URI para cumplir con CSP
      // Apple Store: https://apps.apple.com/cl/app/firmaki-id/id6756390157
      // Google Play: https://play.google.com/store/apps/details?id=id.amparo.firmaki
      // Usamos links directos en los botones ya que CSP bloquea imágenes externas
      this.qrAppleStore = '';
      this.qrPlayStore = '';
    },
    startPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval);
      this.pollingInterval = setInterval(() => {
        this.pollStatus();
      }, 5000);
    },
    async pollStatus() {
      const { idProceso } = this.$route.query;
      try {
        const response = await cuVerificationInfoExternal({
          params: {
            tipo: 'signapi',
            accion: 'status',
          },
          headers: {
            Authorization: this.token,
          },
          body: {
            idProceso,
          }
        });
        const status = response.data && response.data.status;
        if (status === 'CERTIFICATE_CREATED') {
          this.stopPolling();
          this.verificationSuccess();
        } else if (status === 'CERTIFICATE_QUEUED') {
          this.certificateQueued = true;
        } else if (status === 'NFC_SUCCESS') {
          // NFC validada, solicitar preguntas de seguridad
          this.stopPolling();
          this.loadingQuestions = true;
          await this.requestCertificateGeneration();
        } else if (status === 'SUCCESS') {
          // Verificación de identidad exitosa, esperando generación de certificado
          this.certificateQueued = true;
        } else if (status && status !== 'INITIATED') {
          this.stopPolling();
          this.verificationError(status);
        }
      } catch (error) {
        // Silenciar errores de polling, seguir intentando
      }
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async requestCertificateGeneration() {
      const { idProceso } = this.$route.query;
      try {
        const response = await cuVerificationInfoExternal({
          params: {
            tipo: 'signapi',
            accion: 'request_questions',
          },
          headers: {
            Authorization: this.token,
          },
          body: {
            idProceso,
          }
        });
        this.loadingQuestions = false;
        // Mostrar preguntas de seguridad
        if (response.data && response.data.preguntas) {
          this.securityQuestions = response.data.preguntas;
          this.securityAnswers = {};
          this.showSecurityQuestions = true;
        } else {
          this.verificationError('NO_QUESTIONS');
        }
      } catch (error) {
        this.loadingQuestions = false;
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al solicitar preguntas de verificación',
          { title: 'Error', variant: 'danger', solid: true, noAutoHide: true }
        );
        this.verificationError('QUESTIONS_ERROR');
      }
    },
    async submitSecurityAnswers() {
      const { idProceso } = this.$route.query;
      this.loading = true;
      try {
        // Formatear respuestas con questionId y answerId
        const respuestas = Object.entries(this.securityAnswers).map(([index, answerId]) => ({
          questionId: this.securityQuestions[index]?.questionId || index,
          answerId: answerId
        }));

        await cuVerificationInfoExternal({
          params: {
            tipo: 'signapi',
            accion: 'answer_questions',
          },
          headers: {
            Authorization: this.token,
          },
          body: {
            idProceso,
            respuestas,
          }
        });

        this.loading = false;
        this.showSecurityQuestions = false;
        this.certificateQueued = true;
        // Reiniciar polling para esperar CERTIFICATE_CREATED
        this.startPolling();
      } catch (error) {
        this.loading = false;
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al validar respuestas',
          { title: 'Error', variant: 'danger', solid: true, noAutoHide: true }
        );
      }
    },
    formatQuestionOptions(options) {
      if (!options) return [];
      if (Array.isArray(options)) {
        return options.map(opt => ({
          text: opt.value || opt.text || opt.label || opt,
          value: opt.answerId || opt.id || opt.value || opt
        }));
      }
      return Object.entries(options).map(([key, val]) => ({
        text: val.value || val.text || val.label || val,
        value: val.answerId || val.id || key
      }));
    },
    verificationSuccess() {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.stopPolling();
      this.$bvToast.toast(
        'Verificación de identidad completada exitosamente',
        {
          title: 'Verificación exitosa',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        }
      );
      // Navegar al documento para firmar
      const { token } = this.$route.params;
      const { idProceso } = this.$route.query;
      setTimeout(() => {
        this.$router.push({
          name: 'signature-external-file',
          params: { token },
          query: { idProceso, provider: 'signapi' }
        });
      }, 2000);
    },
    verificationError(status) {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.stopPolling();
      this.verificationFailed = true;
      this.verificationErrorStatus = status;
      this.$bvToast.toast(
        `La verificación de identidad falló (${status}). Intente nuevamente.`,
        {
          title: 'Error en verificación',
          variant: 'danger',
          solid: true,
          noAutoHide: true
        }
      );
    },
    retryVerification() {
      // Si no tenemos idProceso (llegamos con error desde CU/SignApi), redirigir al inicio
      if (!this.$route.query.idProceso) {
        const { token } = this.$route.params;
        this.$router.push({
          name: 'signature-external',
          params: { token }
        });
        return;
      }
      this.verificationStarted = false;
      this.verificationFailed = false;
      this.verificationErrorStatus = '';
      this.verificationMethod = '';
      this.certificateQueued = false;
      this.showSecurityQuestions = false;
      this.loadingQuestions = false;
      this.securityQuestions = [];
      this.securityAnswers = {};
      this.countdownExpired = false;
    },
    async getProcessInfo() {
      try {
        this.loading = true;
        this.$bvToast.toast(
          'Estamos buscando la información necesaria, espera unos segundos',
          {
            title: '¡Hola!',
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000
          }
        );
        const idProceso = this.$route.query.idProceso;
        const response = await this.getCUInfo({
          processId: idProceso,
          action: 'info',
          domain: 'portal.escritoriodigital.cl',
          token: this.token,
        });
        this.loading = false;
        const { info } = response;
        if (info.name) {
          this.isAuthorized = true;
        }
        // Feature flags
        if (response.nfcEnabled !== undefined) this.nfcEnabled = response.nfcEnabled;
        if (response.bankTransferEnabled !== undefined) this.bankTransferEnabled = response.bankTransferEnabled;

        this.cuUserData = {
          iut: `${info.RolUnico.numero}-${info.RolUnico.DV}`,
          name: info.name.nombres.join(' '),
          lastName: info.name.apellidos[0],
          mothersLastName: info.name.apellidos[1] || '',
          address: '',
          iutNumber: '',
          email: '',
          landline: '',
          phone: '',
          civilStatus: '',
          birthday: ''
        };
      } catch (error) {
        console.error(error);
        this.loading = false;
      }
    }
  },
  async created() {
    this.jwtData = decodeJwt(this.token);
    this.provider = this.jwtData.provider || 'esign7';

    // Si viene con status=error (redirect desde SignApi/CU por error), mostrar vista de fallo
    if (this.$route.query.status === 'error') {
      this.verificationStarted = true;
      this.verificationFailed = true;
      this.verificationErrorStatus = 'ENROLLMENT_ERROR';
      return;
    }

    await this.getProcessInfo();

    // Si viene de callback de transferencia bancaria, iniciar polling
    if (this.$route.query.pendingVerification === 'bank') {
      this.verificationStarted = true;
      this.verificationMethod = 'BANK_TRANSFER';
      // Extraer idProceso del URL (puede estar en query o embebido en otro param)
      if (!this.$route.query.idProceso) {
        const url = window.location.href;
        const match = url.match(/idProceso[=:]([a-f0-9-]+)/i);
        if (match) {
          this.$route.query.idProceso = match[1];
        }
      }
      this.startPolling();
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }
}
</script>

<style scoped>
.btn-outline-primary:hover {
  color: #fff !important;
}
</style>
